// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import ValidationBadge from '../ValidationBadge';
import type { ViewPrefs } from '../../../store/vaccineClientStore';
import type { ValidationIssue } from '../../../lib/validation';

export interface DecisionTreeToolbarProps {
  onCreateBlank: () => void;
  onLoadTree: () => void;
  onSaveTree: () => void;
  onExportPNG: () => void;
  onToggleFullscreen: () => void;
  onShowHelp: () => void;
  onCompute?: () => void;
  computeLoading?: boolean;
  computeError?: string | null;
  view: ViewPrefs;
  onViewChange: (view: Partial<ViewPrefs>) => void;
  issues: ValidationIssue[];
  onShowValidationDetails: () => void;
}

/**
 * Presentational toolbar component for DecisionTreeBuilder
 * Handles all toolbar buttons and controls
 */
export const DecisionTreeToolbar: React.FC<DecisionTreeToolbarProps> = ({
  onCreateBlank,
  onLoadTree,
  onSaveTree,
  onExportPNG,
  onToggleFullscreen,
  onShowHelp,
  onCompute,
  computeLoading,
  computeError,
  view,
  onViewChange,
  issues,
  onShowValidationDetails,
}) => {
  return (
    <div className="decision-tree-toolbar" role="toolbar" aria-label="Decision tree toolbar">
      <div className="toolbar-left">
        <button 
          className="btn btn-secondary" 
          onClick={onCreateBlank}
          title="Create blank decision tree"
          aria-label="Create blank decision tree"
        >
          Blank tree
        </button>
        {/* {onCompute && (
          <button 
            className="btn btn-primary" 
            onClick={onCompute}
            disabled={computeLoading}
            title={computeLoading ? "Computing..." : "Compute decision tree with R backend"}
            aria-label="Compute decision tree"
          >
            {computeLoading ? "Computing..." : "Compute"}
          </button>
        )} */}
        <button 
          className="btn btn-secondary" 
          onClick={onLoadTree}
          title="Load tree from JSON file"
          aria-label="Load tree from JSON file"
        >
          Load tree
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={onSaveTree}
          title="Save tree to JSON file"
          aria-label="Save tree to JSON file"
        >
          Save tree
        </button>

        <button 
          className="btn btn-secondary" 
          onClick={onExportPNG}
          title="Export as PNG"
          aria-label="Export as PNG"
        >
          Export PNG
        </button>

        <button 
          className="btn btn-secondary" 
          onClick={onToggleFullscreen}
          title={view.fullscreen ? 'Exit fullscreen (F)' : 'Enter fullscreen (F)'}
          aria-label={view.fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {view.fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={onShowHelp}
          title="Show help (?)"
          aria-label="Show help"
        >
          Help
        </button>
      </div>
      
      <div className="toolbar-right">
        <div className="toolbar-toggles">
          <label className="toggle-label" htmlFor="show-probabilities">
            <input
              id="show-probabilities"
              name="show-probabilities"
              type="checkbox"
              checked={view.showProb}
              onChange={(e) => onViewChange({ showProb: e.target.checked })}
              aria-label="Show probabilities"
            />
            Show probabilities
          </label>
          <label className="toggle-label" htmlFor="show-costs">
            <input
              id="show-costs"
              name="show-costs"
              type="checkbox"
              checked={view.showCost}
              onChange={(e) => onViewChange({ showCost: e.target.checked })}
              aria-label="Show costs"
            />
            Show costs
          </label>
          <label className="toggle-label" htmlFor="show-qalys">
            <input
              id="show-qalys"
              name="show-qalys"
              type="checkbox"
              checked={view.showQaly}
              onChange={(e) => onViewChange({ showQaly: e.target.checked })}
              aria-label="Show QALYs"
            />
            Show QALYs
          </label>
        </div>
        
        <div className="node-size-control">
          <label htmlFor="node-size-select">Node size:</label>
          <select
            id="node-size-select"
            value={view.nodeSize}
            onChange={(e) => onViewChange({ nodeSize: e.target.value as 's' | 'm' | 'l' })}
            aria-label="Node size"
          >
            <option value="s">S</option>
            <option value="m">M</option>
            <option value="l">L</option>
          </select>
        </div>
        
        <ValidationBadge 
          issues={issues}
          onClick={onShowValidationDetails}
        />
      </div>
    </div>
  );
};

